import React, {FC, useContext, useEffect, useMemo, useState} from 'react';
import {Images} from '../../../../assets';
import SkillSection from '../skill-section';
import {AccountContextInterface, AccountContext} from '../../../../contexts';
import {Account} from '../../../../models';
import {WorkExperience, WorkExperienceType} from '../../../../models/work-experience.model';
import {parseEmploymentTenure, formatTimelineRange} from '../../../../utils';
import {ExpandableText} from '../../../../components/expandable-text';

interface ExperienceEducationProps {
  account: Account;
}

type ViewType = 'EXPERIENCE' | 'EDUCATION' | 'SKILL';

const ExperienceEducation: FC<ExperienceEducationProps> = ({account}) => {
  const {accountService} = useContext<AccountContextInterface>(AccountContext);
  const [workExperiences, setWorkExperiences] = useState<WorkExperience[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState<ViewType>('EXPERIENCE');

  useEffect(() => {
    const fetchWorkExperiences = async () => {
      try {
        setLoading(true);
        const {data: organizations} = await accountService.getAccountOrganizations(account.id, 1, 100);
        if (organizations && organizations.length > 0) {
          const allExperiences: WorkExperience[] = [];
          for (const org of organizations) {
            const {data: experiences} = await accountService.getWorkExperiences(account.id, org.organization.id);
            if (experiences) {
              allExperiences.push(...experiences);
            }
          }
          setWorkExperiences(allExperiences);
        }
      } catch (error) {
        console.error('Error fetching work experiences:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkExperiences();
  }, [account.id, accountService]);

  const sortedExperiences = useMemo(() => {
    return [...workExperiences].sort((a, b) => {
      const tenureA = parseEmploymentTenure(a.employmentTenure);
      const tenureB = parseEmploymentTenure(b.employmentTenure);
      const startA = tenureA.startDate ? tenureA.startDate.getTime() : -Infinity;
      const startB = tenureB.startDate ? tenureB.startDate.getTime() : -Infinity;
      return startB - startA;
    });
  }, [workExperiences]);

  const education = sortedExperiences.filter(exp => exp.type === WorkExperienceType.EDUCATION);
  const experience = sortedExperiences.filter(exp => exp.type === WorkExperienceType.EXPERIENCE);

  const currentItems = activeView === 'EXPERIENCE' ? experience : activeView === 'EDUCATION' ? education : [];
  const emptyStateLabel = activeView === 'EXPERIENCE' ? 'experience' : 'education';

  return (
    <section className="flex flex-col gap-6">
      <div className="rounded-3xl border border-gray-100 bg-white px-3.5 py-4 shadow-sm">
        <div className="mb-6 flex gap-2 rounded-full bg-gray-100 p-1 shadow-inner">
          <button
            onClick={() => setActiveView('EXPERIENCE')}
            className={`flex-1 min-w-[100px] rounded-full px-3 py-2 text-sm font-semibold text-center transition-all ${
              activeView === 'EXPERIENCE' ? 'bg-blue-600 text-white shadow' : 'text-gray-700 hover:text-gray-900'
            }`}>
            Experience
          </button>

          <button
            onClick={() => setActiveView('EDUCATION')}
            className={`flex-1 min-w-[100px] rounded-full px-3 py-2 text-sm font-semibold text-center transition-all ${
              activeView === 'EDUCATION' ? 'bg-blue-600 text-white shadow' : 'text-gray-700 hover:text-gray-900'
            }`}>
            Education
          </button>

          <button
            onClick={() => setActiveView('SKILL')}
            className={`flex-1 min-w-[100px] rounded-full px-3 py-2 text-sm font-semibold text-center transition-all ${
              activeView === 'SKILL' ? 'bg-blue-600 text-white shadow' : 'text-gray-700 hover:text-gray-900'
            }`}>
            Skills
          </button>
        </div>
        {loading && workExperiences.length === 0 ? (
          <div className="flex min-h-[200px] items-center justify-center rounded-3xl">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600" />
          </div>
        ) : (
          <>
            {activeView === 'SKILL' ? (
              <div className="-mx-6 -mb-6 max-h-[60vh] overflow-y-auto px-6 pb-6">
                <SkillSection accountId={account.id} onSkillClick={() => {}} variant="embedded" />
              </div>
            ) : currentItems.length > 0 ? (
              <div className="flex flex-col">
                {currentItems.map((item, index) => (
                  <div key={item.id}>
                    <div className="flex flex-col gap-1.5">
                      <h4 className="text-lg font-semibold text-gray-900">{item.organization?.name}</h4>

                      <p className="text-sm text-gray-700">{item.jobTitle.name}</p>

                      <p className="text-sm text-gray-500">{formatTimelineRange(item.employmentTenure)}</p>

                      {item.jobDescription && (
                        <div className="mt-1">
                          <ExpandableText maxLength={150} className="text-sm text-gray-600 leading-relaxed">
                            {item.jobDescription}
                          </ExpandableText>
                        </div>
                      )}
                    </div>

                    {index < currentItems.length - 1 && <div className="mt-2 mb-2 border-t border-gray-200" />}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col gap-4 justify-center items-center py-10">
                <img src={Images.NO_ACTIVITY} alt={`No ${emptyStateLabel}`} className="h-48 w-48" />
                <span className="text-sm text-gray-600">{`This Aura has not added any ${emptyStateLabel} yet.`}</span>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default ExperienceEducation;
